import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { TextAlign } from '@tiptap/extension-text-align';
import { Typography } from '@tiptap/extension-typography';

interface RichTextViewerProps {
  content: string;
  className?: string;
}

const RichTextViewer: React.FC<RichTextViewerProps> = ({ content, className = "" }) => {
  // 配置编辑器扩展
  const extensions = [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3, 4, 5, 6],
      },
      bulletList: {
        keepMarks: true,
        keepAttributes: false,
      },
      orderedList: {
        keepMarks: true,
        keepAttributes: false,
      },
    }),
    Image.configure({
      HTMLAttributes: {
        class: 'rich-text-image',
      },
      inline: true,
      allowBase64: true,
    }),
    Link.configure({
      HTMLAttributes: {
        class: 'rich-text-link',
      },
      openOnClick: true,
    }),
    TextStyle,
    Color,
    Highlight.configure({
      multicolor: true,
    }),
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Typography,
  ];

  // 只读模式的编辑器，用于渲染内容
  const editor = useEditor({
    extensions,
    content: content || '',
    editable: false,
    immediatelyRender: false,
  });

  return (
    <div className={`rich-text-viewer prose max-w-none ${className}`}>
      <style jsx>{`
        .rich-text-viewer {
          font-family: system-ui, -apple-system, sans-serif;
          line-height: 1.6;
          color: hsl(var(--foreground));
        }

        /* 标题样式 */
        .rich-text-viewer :global(h1) {
          font-size: 2rem;
          font-weight: 700;
          margin: 1.5em 0 0.5em 0;
          color: hsl(var(--primary));
        }
        
        .rich-text-viewer :global(h2) {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 1.3em 0 0.5em 0;
          color: hsl(var(--primary));
          border-bottom: 2px solid hsl(var(--border));
          padding-bottom: 0.3em;
        }
        
        .rich-text-viewer :global(h3) {
          font-size: 1.25rem;
          font-weight: 600;
          margin: 1.2em 0 0.5em 0;
          color: hsl(var(--primary));
        }

        /* 段落样式 */
        .rich-text-viewer :global(p) {
          margin: 1em 0;
          text-align: justify;
        }

        /* 列表样式 */
        .rich-text-viewer :global(ul, ol) {
          margin: 1em 0;
          padding-left: 1.5em;
        }

        .rich-text-viewer :global(li) {
          margin: 0.5em 0;
        }

        /* 图片样式 */
        .rich-text-viewer :global(.rich-text-image) {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
          margin: 1em 0;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          cursor: pointer;
          transition: transform 0.2s ease-in-out;
        }

        .rich-text-viewer :global(.rich-text-image:hover) {
          transform: scale(1.02);
        }

        /* 链接样式 */
        .rich-text-viewer :global(.rich-text-link) {
          color: hsl(var(--primary));
          text-decoration: underline;
          text-decoration-color: hsl(var(--primary));
          text-underline-offset: 2px;
          transition: all 0.2s ease-in-out;
        }

        .rich-text-viewer :global(.rich-text-link:hover) {
          color: hsl(var(--primary));
          text-decoration-thickness: 2px;
        }

        /* 代码块样式 */
        .rich-text-viewer :global(pre) {
          background-color: hsl(var(--muted));
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          padding: 1em;
          margin: 1em 0;
          overflow-x: auto;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.9em;
        }

        .rich-text-viewer :global(code) {
          background-color: hsl(var(--muted));
          padding: 0.2em 0.4em;
          border-radius: 3px;
          font-family: 'Monaco', 'Menlo', monospace;
          font-size: 0.9em;
        }

        /* 引用样式 */
        .rich-text-viewer :global(blockquote) {
          border-left: 4px solid hsl(var(--primary));
          padding-left: 1em;
          margin: 1em 0;
          font-style: italic;
          background-color: hsl(var(--muted) / 0.5);
          border-radius: 0 4px 4px 0;
          padding: 1em;
        }

        /* 高亮样式 */
        .rich-text-viewer :global(mark) {
          background-color: hsl(var(--accent));
          padding: 0.1em 0.2em;
          border-radius: 2px;
        }

        /* 表格样式 */
        .rich-text-viewer :global(table) {
          width: 100%;
          border-collapse: collapse;
          margin: 1em 0;
          border: 1px solid hsl(var(--border));
          border-radius: 6px;
          overflow: hidden;
        }

        .rich-text-viewer :global(th, td) {
          border: 1px solid hsl(var(--border));
          padding: 0.75em;
          text-align: left;
        }

        .rich-text-viewer :global(th) {
          background-color: hsl(var(--muted));
          font-weight: 600;
        }

        .rich-text-viewer :global(tr:nth-child(even)) {
          background-color: hsl(var(--muted) / 0.3);
        }

        /* 分割线样式 */
        .rich-text-viewer :global(hr) {
          border: none;
          height: 2px;
          background: linear-gradient(to right, transparent, hsl(var(--border)), transparent);
          margin: 2em 0;
        }

        /* 响应式设计 */
        @media (max-width: 768px) {
          .rich-text-viewer :global(h1) {
            font-size: 1.5rem;
          }
          
          .rich-text-viewer :global(h2) {
            font-size: 1.25rem;
          }
          
          .rich-text-viewer :global(h3) {
            font-size: 1.125rem;
          }

          .rich-text-viewer :global(.rich-text-image) {
            margin: 0.5em 0;
          }
        }
      `}</style>
      
      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextViewer;